import React, { useState, useRef, useEffect } from 'react';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import RatingReviewLabels from '@tcp/core/src/components/features/account/RatingReview/RatingReview.labels';
import {
  getStoreFilterData,
  getStoreId,
} from '@tcp/core/src/components/features/browse/ShopThisStore/container/ShopThisStore.utils';
import {
  STORE_DETAILS_LABELS,
  StoreLocatorLabels,
  lblLimitedEditions,
  lblStoreSpecials,
} from '@tcp/core/src/components/features/storeLocator/Labels/StoreLocator.labels.js';
import LoaderSkelton from '@tcp/core/src/components/common/molecules/LoaderSkelton';
import styles from '../styles/StoreSpecialCarousel.style';
import StoreSpecialTile from '../StoreSpecialTile';
import Carousel from '../../../../../../common/molecules/ModuleTabbedCarousel/molecules/Carousel';
import {
  ARROW_VISIBLE_STORE_SPECIALS,
  calculateBreakPointByWidth,
  SLIDES_TO_SCROLL_BP,
} from '../../../../../../common/molecules/ModuleTabbedCarousel/molecules/CarouselTab/config/CarouselTab.config';
import { isMobileWeb } from '../../../../../../../utils';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';

// eslint-disable-next-line sonarjs/cognitive-complexity
const StoreSpecialCarousel = (props) => {
  const {
    className,
    onQuickViewOpenClick,
    promotionsData,
    serverTime,
    isBuyMorePromoEnabled,
    isTileRedesignAB,
    isUpdatedPromotionsDisplayEnabled,
    isStoreSpecialProductsOnStoreDetails,
    isStoreSpecialsPriceOn,
    storeSpecialDiscount,
    fetchStoreSpecialProducts,
    store,
    storeSpecialProducts,
    setFavoriteStore,
    isStoreSpecialProductsLoading,
  } = props;
  const [hasMounted, setHasMounted] = useState(false);
  const productTileRef = useRef(null);

  useEffect(() => {
    const storeId = store?.basicInfo?.id;
    setHasMounted(true);
    if (storeId && isStoreSpecialProductsOnStoreDetails) {
      fetchStoreSpecialProducts({ store });
    }
  }, [
    store?.basicInfo?.storeDiscount,
    store?.basicInfo?.id,
    fetchStoreSpecialProducts,
    isStoreSpecialProductsOnStoreDetails,
  ]);

  const totalProducts = storeSpecialProducts?.length || 0;
  const breakPointVal = hasMounted
    ? calculateBreakPointByWidth([window?.innerWidth])
    : calculateBreakPointByWidth([1444]);

  const cartItemTileLabels = getLabelFromCopyOrFromTranslation(
    'global.cartItemTile',
    RatingReviewLabels
  );

  if (!isStoreSpecialProductsOnStoreDetails) {
    return null;
  }

  const showSkeleton =
    store?.basicInfo?.storeDiscount &&
    store?.basicInfo?.id &&
    (isStoreSpecialProductsLoading || !hasMounted) &&
    totalProducts <= 0;

  if ((!hasMounted || totalProducts <= 0) && !showSkeleton) {
    return null;
  }

  const handleLinkClick = () => {
    setFavoriteStore(store);
  };

  return (
    <div className={className}>
      <div className="store_specials_header">
        <div>
          <BodyCopy
            className="store_specials_heading"
            data-locator="store_specials_heading"
            component="p"
            fontSize={isMobileWeb() ? 'fs16' : 'fs18'}
            fontFamily="secondary"
            fontWeight="bold"
          >
            {renderLabel(
              getLabelFromCopyOrFromTranslation(
                `${STORE_DETAILS_LABELS}.${lblStoreSpecials}`,
                StoreLocatorLabels
              ),
              'lbl_Store_Specials'
            )}
          </BodyCopy>
          <BodyCopy
            className="store_specials_subheading"
            data-locator="store_specials_subheading"
            component="p"
            fontSize={isMobileWeb() ? 'fs12' : 'fs14'}
            fontFamily="secondary"
            fontWeight="regular"
          >
            {renderLabel(
              getLabelFromCopyOrFromTranslation(
                `${STORE_DETAILS_LABELS}.${lblLimitedEditions}`,
                StoreLocatorLabels
              ),
              'lbl_Limited_Editions'
            )}
          </BodyCopy>
        </div>
        <div>
          {!showSkeleton && (
            <Anchor
              underline
              anchorVariation="primary"
              fontSize={isMobileWeb() ? 'fs12' : 'fs16'}
              fontFamily="secondary"
              dataLocator="store_special-view_all"
              className="store_special-view_all"
              textAlign="right"
              asPath={`/search/shop-this-store?bopisStoreId=${encodeURIComponent(
                getStoreFilterData(store)
              )}&v_storeEligibleIds=${getStoreId(store)}`}
              to={`/search?search/shop-this-store?searchQuery=*&bopisStoreId=${encodeURIComponent(
                getStoreFilterData(store)
              )}&v_storeEligibleIds=${getStoreId(store)}`}
              onClick={handleLinkClick}
            >
              {renderLabel(cartItemTileLabels.lbl_view_all, 'lbl_view_all')}
            </Anchor>
          )}
        </div>
      </div>

      <Carousel
        key={`product-carousel`}
        isArrowVisible={totalProducts > ARROW_VISIBLE_STORE_SPECIALS[breakPointVal]}
        tileRef={productTileRef}
        defaultTileWidth={265}
        slidesToScroll={SLIDES_TO_SCROLL_BP[breakPointVal]}
        isCategoryTab={false}
        customCls="storeSpecialCaraosal"
      >
        {totalProducts > 0 &&
          storeSpecialProducts?.map((product, index) => (
            <StoreSpecialTile
              key={index} // Added key prop for list items
              index={index}
              product={product}
              onQuickViewOpenClick={onQuickViewOpenClick}
              promotionsData={promotionsData}
              serverTime={serverTime}
              isBuyMorePromoEnabled={isBuyMorePromoEnabled}
              isTileRedesignAB={isTileRedesignAB}
              isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
              isStoreSpecialsPriceOn={isStoreSpecialsPriceOn}
              storeSpecialDiscount={storeSpecialDiscount}
              currentStore={store}
              setFavoriteStore={setFavoriteStore}
            />
          ))}
        {showSkeleton &&
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="skeleton-container">
              <div className="skeleton-wrapper">
                <LoaderSkelton className="skeleton-image" />
                <div className="skeleton-atb">
                  <LoaderSkelton className="skeleton-price" />
                  <LoaderSkelton className="skeleton-cart-icon" />
                </div>
                <LoaderSkelton className="skeleton-review" />
              </div>
            </div>
          ))}
      </Carousel>
    </div>
  );
};

export default withStyles(StoreSpecialCarousel, styles);

export { StoreSpecialCarousel as StoreSpecialCarouselVanilla };
